import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom"; // --- ADDED NAVIGATE ---
import { useAppId } from "../../context/AppIdContext";
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const DateWiseSlipSendDash = () => {
    // --- Context & State ---
    const { appId } = useAppId();
    const navigate = useNavigate(); // --- INIT NAVIGATE ---
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);
    
    // Generate Month Options (e.g., "Jan,2026", "Feb,2026")
    const generateMonthOptions = () => {
        const options = [];
        const date = new Date();
        // Generate a reasonable range (6 months back, 6 months forward)
        for (let i = -6; i <= 6; i++) {
            const d = new Date(date.getFullYear(), date.getMonth() + i, 1);
            const monthStr = d.toLocaleString('en-US', { month: 'short' });
            const year = d.getFullYear();
            options.push(`${monthStr},${year}`);
        }
        return options;
    };

    const [monthOptions] = useState(generateMonthOptions());
    
    // Default to current month (e.g., "Mar,2026")
    const getCurrentMonthString = () => {
        const date = new Date();
        const monthStr = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${monthStr},${year}`;
    };

    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthString());

    // --- Fetch Data ---
    useEffect(() => {
        if (appId && selectedMonth) {
            fetchDateWiseData();
        }
    }, [appId, selectedMonth]);

    const fetchDateWiseData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_date_wise_slip_distribution`, {
                app_id: appId,
                month: selectedMonth
            });

            if (response.data.success && response.data.data && response.data.data.Success === "1") {
                const data = response.data.data.result || [];
                setChartData(data);
            } else {
                setChartData([]);
            }
        } catch (error) {
            console.error("Error fetching date wise data:", error);
            setChartData([]);
        } finally {
            setLoading(false);
        }
    };

    // Calculate Max Value for Chart Scaling
    const maxTotal = Math.max(...chartData.map(d => d.total), 20); // At least 20 for visual scale
    const yAxisMarkers = [
        maxTotal,
        Math.round(maxTotal * 0.75),
        Math.round(maxTotal * 0.5),
        Math.round(maxTotal * 0.25),
        0
    ];

    // Helper to format date string ("01 Mar, 2026" -> "01")
    const getDayFromDate = (dateStr) => {
        if (!dateStr) return "";
        return dateStr.split(" ")[0];
    };

    // Helper to format dropdown display ("Mar,2026" -> "Mar 2026")
    const formatDisplayMonth = (monthStr) => {
        return monthStr.replace(",", " ");
    };

    return (
        <div className="dashboard-container">
            {/* Header */}
            <div className="dashboard-header-wrapper">
                <Header title={"Date Wise Slip Status"} />
            </div>

            <main className="dashboard-main">
                <div className="page-header slide-up-element">
                    <div>
                        <p className="page-subtitle">Track daily slip distribution for the selected month.</p>
                    </div>
                </div>

                <div className="row g-4 mt-1">
                    {/* LEFT COLUMN - CHART */}
                    <div className="col-lg-8 col-md-12 slide-up-element" style={{ animationDelay: '0.1s' }}>
                        <div className="modern-card chart-card">
                            <div className="card-header">
                                <h3 className="card-title">Date Wise Distribution</h3>
                                <div className="month-selector">
                                    <select 
                                        value={selectedMonth} 
                                        onChange={(e) => setSelectedMonth(e.target.value)}
                                        className="custom-select"
                                    >
                                        {monthOptions.map(opt => (
                                            <option key={opt} value={opt}>{formatDisplayMonth(opt)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {loading ? (
                                <div className="loader-container">
                                    <div className="spinner"></div>
                                </div>
                            ) : chartData.length > 0 ? (
                                <div className="custom-chart-wrapper">
                                    {/* Y-Axis */}
                                    <div className="y-axis">
                                        {yAxisMarkers.map((marker, i) => (
                                            <div key={i} className="y-axis-label">{marker}</div>
                                        ))}
                                    </div>

                                    {/* Chart Area */}
                                    <div className="chart-area">
                                        {/* Grid Lines */}
                                        <div className="grid-lines">
                                            {yAxisMarkers.map((_, i) => (
                                                <div key={i} className="grid-line"></div>
                                            ))}
                                        </div>

                                        {/* Bars Container */}
                                        <div className="bars-container">
                                            {chartData.map((item, index) => {
                                                const heightPercent = (item.total / maxTotal) * 100;
                                                const day = getDayFromDate(item.date);
                                                
                                                return (
                                                    <div key={index} className="bar-group">
                                                        <div className="bar-value" style={{ opacity: item.total > 0 ? 1 : 0 }}>
                                                            {item.total > 0 ? item.total.toFixed(1) : "0.0"}
                                                        </div>
                                                        <div className="bar-track">
                                                            <div 
                                                                className="bar-fill" 
                                                                style={{ height: `${heightPercent}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="x-axis-label">{day}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    
                                    <div className="chart-legend">
                                        <span className="legend-dot"></span>
                                        <span className="legend-text">Voters Sent</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="no-data-msg">No data available for {formatDisplayMonth(selectedMonth)}</div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT COLUMN - TABLE */}
                    <div className="col-lg-4 col-md-12 slide-up-element" style={{ animationDelay: '0.2s' }}>
                        <div className="modern-card table-card">
                            <div className="table-container">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th className="text-center">Slip</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="2" className="text-center p-4">Loading...</td>
                                            </tr>
                                        ) : chartData.length > 0 ? (
                                            chartData.map((item, index) => (
                                                <tr 
                                                    key={index} 
                                                    // --- THE FIX: NAVIGATION ADDED HERE ---
                                                    onClick={() => navigate('/date_wise_slip_sending_voter', { state: { date: item.date } })}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td className="date-cell">{item.date}</td>
                                                    <td className="text-center value-cell">
                                                        <span className={`status-pill ${item.total > 0 ? 'active' : 'inactive'}`}>
                                                            {item.total}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="2" className="text-center p-4 text-muted">No records found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <style jsx>{`
                /* --- KEYFRAMES --- */
                @keyframes slideInUp {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes growUp {
                    from { height: 0; opacity: 0; }
                    to { opacity: 1; }
                }

                /* --- GLOBAL STRUCTURE --- */
                .dashboard-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    width: 100%;
                    box-sizing: border-box;
                }
                
                .dashboard-container * {
                    box-sizing: border-box;
                }

                .slide-up-element {
                    opacity: 0;
                    animation: slideInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                /* Header Wrapper */
                .dashboard-header-wrapper {
                    padding: 16px 20px;
                }
                @media (min-width: 768px) {
                    .dashboard-header-wrapper { padding: 25px 25px 0 25px; }
                }

                /* Dashboard Main */
                .dashboard-main {
                    padding: 12px 16px;
                    max-width: 100%;
                    margin: 0 auto;
                }
                @media (min-width: 768px) {
                    .dashboard-main { padding: 16px 24px; }
                }

                .page-subtitle {
                    font-size: 1.05rem;
                    font-weight: 700;
                    color: #475569;
                    margin: 0;
                }

                /* --- MODERN CARDS --- */
                .modern-card {
                    background: #FFFFFF;
                    border-radius: 20px;
                    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.04);
                    border: 1px solid #F1F5F9;
                    overflow: hidden;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }

                .chart-card {
                    padding: 24px;
                }

                .table-card {
                    padding: 0; /* Table handles its own padding */
                }

                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .card-title {
                    font-size: 1.15rem;
                    font-weight: 800;
                    color: #1E293B;
                    margin: 0;
                }

                /* Custom Select Dropdown */
                .custom-select {
                    appearance: none;
                    background-color: #FFFFFF;
                    border: 1px solid #E2E8F0;
                    border-radius: 10px;
                    padding: 8px 36px 8px 16px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #1E293B;
                    cursor: pointer;
                    outline: none;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E");
                    background-repeat: no-repeat;
                    background-position: right 12px center;
                    background-size: 16px;
                    transition: all 0.2s ease;
                }

                .custom-select:hover {
                    border-color: #CBD5E1;
                }
                .custom-select:focus {
                    border-color: #8B5CF6;
                    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
                }

                /* --- CUSTOM CSS CHART --- */
                .custom-chart-wrapper {
                    position: relative;
                    height: 400px;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                }

                .chart-area {
                    position: relative;
                    flex: 1;
                    margin-left: 40px; /* Space for Y-axis */
                    margin-bottom: 30px; /* Space for X-axis */
                    display: flex;
                }

                /* Y Axis */
                .y-axis {
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 30px;
                    width: 35px;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                .y-axis-label {
                    font-size: 0.75rem;
                    color: #64748B;
                    font-weight: 500;
                    text-align: right;
                    transform: translateY(-50%); /* Center align with grid line */
                }

                /* Grid Lines */
                .grid-lines {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    z-index: 1;
                }

                .grid-line {
                    width: 100%;
                    height: 1px;
                    background-color: #E2E8F0;
                }

                /* Bars Container */
                .bars-container {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    width: 100%;
                    height: 100%;
                    padding: 0 10px;
                    overflow-x: auto;
                    overflow-y: hidden;
                    /* Hide scrollbar for clean look */
                    scrollbar-width: none; 
                    -ms-overflow-style: none;
                }
                .bars-container::-webkit-scrollbar { display: none; }

                .bar-group {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: flex-end;
                    height: 100%;
                    width: 30px; /* Fixed width per bar area */
                    min-width: 30px;
                    position: relative;
                    flex-shrink: 0;
                    margin: 0 4px;
                }

                .bar-value {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #1E293B;
                    margin-bottom: 6px;
                    transition: opacity 0.3s;
                }

                .bar-track {
                    width: 22px;
                    height: 100%;
                    display: flex;
                    align-items: flex-end;
                    border-radius: 4px 4px 0 0;
                }

                .bar-fill {
                    width: 100%;
                    background: linear-gradient(180deg, #A78BFA 0%, #8B5CF6 100%);
                    border-radius: 4px 4px 0 0;
                    animation: growUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    transform-origin: bottom;
                    transition: height 0.5s ease;
                }

                .bar-group:hover .bar-fill {
                    background: linear-gradient(180deg, #C4B5FD 0%, #A78BFA 100%);
                    box-shadow: 0 0 8px rgba(139, 92, 246, 0.4);
                }

                .x-axis-label {
                    position: absolute;
                    bottom: -25px;
                    font-size: 0.75rem;
                    color: #64748B;
                    font-weight: 600;
                    text-align: center;
                    width: 100%;
                }

                /* Legend */
                .chart-legend {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-left: 40px;
                    margin-top: 10px;
                }
                .legend-dot {
                    width: 10px;
                    height: 10px;
                    background-color: #8B5CF6;
                    border-radius: 2px;
                }
                .legend-text {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #475569;
                }

                /* --- TABLE CARD --- */
                .table-container {
                    max-height: 480px;
                    overflow-y: auto;
                    width: 100%;
                }
                
                /* Custom Scrollbar for Table */
                .table-container::-webkit-scrollbar { width: 6px; }
                .table-container::-webkit-scrollbar-track { background: #F8FAFC; }
                .table-container::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 10px; }
                .table-container::-webkit-scrollbar-thumb:hover { background: #94A3B8; }

                .data-table {
                    width: 100%;
                    border-collapse: separate;
                    border-spacing: 0;
                }

                .data-table th {
                    position: sticky;
                    top: 0;
                    background: #F8FAFC;
                    padding: 18px 24px;
                    font-size: 0.8rem;
                    font-weight: 700;
                    color: #475569;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border-bottom: 2px solid #E2E8F0;
                    z-index: 10;
                }

                .data-table td {
                    padding: 16px 24px;
                    border-bottom: 1px solid #F1F5F9;
                    transition: background 0.2s;
                }

                .data-table tr:hover td {
                    background: #F8FAFC;
                }

                .date-cell {
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #1E293B;
                }

                .value-cell {
                    font-weight: 700;
                }

                .status-pill {
                    display: inline-block;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 0.85rem;
                }

                .status-pill.active {
                    background: #EDE9FE;
                    color: #8B5CF6;
                }

                .status-pill.inactive {
                    background: #F1F5F9;
                    color: #64748B;
                }

                /* Utilities */
                .loader-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 400px;
                }

                .spinner {
                    width: 40px; height: 40px;
                    border: 4px solid #F1F5F9;
                    border-top: 4px solid #8B5CF6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                .no-data-msg {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 400px;
                    color: #94A3B8;
                    font-weight: 500;
                }
                
                .text-center { text-align: center; }
            `}</style>
        </div>
    );
};

export default DateWiseSlipSendDash;